<template>
  <div class="monte-carlo-chart">
    <!-- Chart area -->
    <div class="relative">
      <svg :viewBox="`0 0 ${W} ${H}`" class="w-full" :style="{ height: chartHeight + 'px' }">
        <!-- Grid lines -->
        <g class="grid-lines">
          <line
            v-for="tick in yTicks"
            :key="tick"
            :x1="PAD_L"
            :y1="yScale(tick)"
            :x2="W - PAD_R"
            :y2="yScale(tick)"
            stroke="#e2e8f0"
            stroke-width="1"
          />
        </g>

        <!-- Histogram bars -->
        <g class="bars">
          <rect
            v-for="(bar, i) in bars"
            :key="i"
            :x="bar.x"
            :y="bar.y"
            :width="bar.w - 1"
            :height="bar.h"
            :fill="bar.highlight ? '#f59e0b' : (bar.investbuddy ? '#10b981' : '#94a3b8')"
            :fill-opacity="bar.highlight ? 1 : (bar.investbuddy ? 1 : 0.55)"
            rx="2"
          />
        </g>

        <!-- InvestBuddy marker line -->
        <line
          :x1="investbuddyX"
          :y1="PAD_T"
          :x2="investbuddyX"
          :y2="H - PAD_B"
          stroke="#10b981"
          stroke-width="2.5"
          stroke-dasharray="6,3"
        />

        <!-- InvestBuddy label -->
        <g :transform="`translate(${investbuddyX}, ${PAD_T - 6})`">
          <rect x="-52" y="-22" width="104" height="22" rx="4" fill="#10b981" />
          <text x="0" y="-5" text-anchor="middle" fill="white" font-size="11" font-weight="700" font-family="system-ui">
            InvestBuddy +106.12%
          </text>
        </g>

        <!-- VOO/median marker -->
        <line
          :x1="vooX"
          :y1="PAD_T"
          :x2="vooX"
          :y2="H - PAD_B"
          stroke="#64748b"
          stroke-width="1.5"
          stroke-dasharray="4,3"
        />
        <g :transform="`translate(${vooX}, ${H - PAD_B + 16})`">
          <text x="0" y="0" text-anchor="middle" fill="#64748b" font-size="10" font-family="system-ui">VOO +42.71%</text>
        </g>

        <!-- X axis label -->
        <line :x1="PAD_L" :y1="H - PAD_B" :x2="W - PAD_R" :y2="H - PAD_B" stroke="#cbd5e1" stroke-width="1" />

        <!-- X axis ticks -->
        <g v-for="tick in xTicks" :key="tick">
          <line :x1="xScale(tick)" :y1="H - PAD_B" :x2="xScale(tick)" :y2="H - PAD_B + 4" stroke="#94a3b8" stroke-width="1" />
          <text :x="xScale(tick)" :y="H - PAD_B + 15" text-anchor="middle" fill="#94a3b8" font-size="9" font-family="system-ui">
            {{ tick > 0 ? '+' : '' }}{{ tick }}%
          </text>
        </g>

        <!-- Y axis ticks -->
        <g v-for="tick in yTicks" :key="`y${tick}`">
          <text :x="PAD_L - 6" :y="yScale(tick) + 4" text-anchor="end" fill="#94a3b8" font-size="9" font-family="system-ui">
            {{ tick }}
          </text>
        </g>
      </svg>
    </div>

    <!-- Legend -->
    <div class="flex flex-wrap items-center justify-center gap-6 mt-4 text-sm">
      <div class="flex items-center gap-2">
        <div class="w-4 h-3 rounded-sm bg-slate-400 opacity-55"></div>
        <span class="text-slate-600">1,000 random 5-stock portfolios</span>
      </div>
      <div class="flex items-center gap-2">
        <div class="w-4 h-3 rounded-sm bg-amber-400"></div>
        <span class="text-slate-600">Median random (+50.72%)</span>
      </div>
      <div class="flex items-center gap-2">
        <div class="w-1 h-4 bg-emerald-500" style="border: 2px dashed #10b981; width: 18px; height: 0; border-bottom: none; border-left: none; border-right: none;"></div>
        <div class="w-4 h-0 border-t-2 border-dashed border-emerald-500"></div>
        <span class="text-slate-600">InvestBuddy (+106.12%)</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

// ── Chart dimensions ──────────────────────────────────────────────
const W = 680
const H = 260
const PAD_L = 36
const PAD_R = 20
const PAD_T = 36
const PAD_B = 30
const chartHeight = 260

// ── Monte Carlo data from PHASE2_SIMULATION_REPORT.md §6.1 ───────
// Distribution approximated from: mean=+64.03%, median=+50.72%, std=68.74%
// 5th pct=-16.10%, 75th pct=+98.47%, 95th pct=+186.04%
// InvestBuddy=+106.12% → 77.7th percentile
// Buckets: [start%, count] — hand-fitted to reported percentiles
const bucketData: Array<[number, number]> = [
  [-80, 2],  [-60, 5],  [-40, 12], [-20, 22], [0, 48],
  [20, 98],  [40, 148], [60, 162], [80, 132], [100, 98],  // InvestBuddy sits between 100-120
  [120, 64], [140, 42], [160, 26], [180, 18], [200, 12],
  [220, 7],  [240, 4],  [260, 2],  [280, 1],  [300, 1],
]
const BUCKET_W = 20  // each bucket spans 20pp
const INVESTBUDDY = 106.12
const VOO = 42.71
const MEDIAN = 50.72

// ── Scales ────────────────────────────────────────────────────────
const xMin = -100
const xMax = 320
const yMax = computed(() => Math.max(...bucketData.map(d => d[1])) * 1.12)

function xScale(v: number) {
  return PAD_L + ((v - xMin) / (xMax - xMin)) * (W - PAD_L - PAD_R)
}
function yScale(v: number) {
  return H - PAD_B - (v / yMax.value) * (H - PAD_T - PAD_B)
}

const xTicks = [-80, -40, 0, 40, 80, 120, 160, 200, 240, 280]
const yTicks = computed(() => {
  const step = 50
  const ticks = []
  for (let i = 0; i <= yMax.value; i += step) ticks.push(i)
  return ticks
})

// ── Bars ──────────────────────────────────────────────────────────
const bars = computed(() =>
  bucketData.map(([start, count]) => {
    const x1 = xScale(start)
    const x2 = xScale(start + BUCKET_W)
    const isMedianBucket = start <= MEDIAN && MEDIAN < start + BUCKET_W
    const isInvestbuddyBucket = start <= INVESTBUDDY && INVESTBUDDY < start + BUCKET_W
    return {
      x: x1,
      y: yScale(count),
      w: x2 - x1,
      h: H - PAD_B - yScale(count),
      highlight: isMedianBucket,
      investbuddy: isInvestbuddyBucket,
    }
  })
)

const investbuddyX = computed(() => xScale(INVESTBUDDY))
const vooX = computed(() => xScale(VOO))
</script>
